// src/hooks/useMeetingLookups.ts
//
// Lookup-driven options for the native meeting forms (replaces the hardcoded
// investmentManagerHouses / bookedByOptions / cbamAttendeesOptions in
// MeetingRequest.tsx). Funds + houses come from the existing reference tables;
// people come from get_meeting_people() (names only — the browser never receives
// calendar/asana emails; those are resolved server-side in the meeting-book fn).

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FundOption {
  id: string;
  fund_name: string;
  fund_manager: string | null;
  fund_class: string | null;
  isin: string | null;
  ocf_pct: number | null;
}

export interface FundHouseOption {
  id: string;
  name: string;
}

// A row from meeting_fund_lookup — the canonical fund reference for the Meeting Request
// picker (thousands of rows; ISIN is the canonical identifier). Distinct from FundOption
// (ba_cbam_funds), which is the currently-held/benchmarked fund universe used elsewhere.
export interface MeetingFundOption {
  id: string;
  isin: string | null;
  fund_name: string;
  fund_house: string | null;
  fund_class: string | null;
  inc_acc: string | null;
  amc: number | null;
  ter_pct: number | null;
  sedol: string | null;
  available_on_abrdn: boolean | null;
}

export interface MeetingPerson {
  id: string;
  display_name: string;
  is_active: boolean;
  meeting_fallback_rank: number | null;
}

// Normalise a fund identifier for comparison (ISIN/SEDOL are case-insensitive, no spaces/dashes).
function normId(v: string | null | undefined): string {
  return (v ?? "").toUpperCase().replace(/[\s-]/g, "");
}

// True if `query` matches the fund by name substring or ISIN (ISIN is the canonical fund
// identifier across the estate). Empty query matches nothing (caller decides "show all" separately).
export function fundMatchesIdentifier(fund: FundOption, query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  const qn = normId(q);
  return (
    fund.fund_name.toLowerCase().includes(q.toLowerCase()) ||
    (!!fund.isin && normId(fund.isin) === qn)
  );
}

export function useMeetingLookups() {
  const funds = useQuery({
    queryKey: ["meeting-lookups", "funds"],
    queryFn: async (): Promise<FundOption[]> => {
      const { data, error } = await supabase
        .from("ba_cbam_funds")
        .select("id, fund_name, fund_manager, fund_class, isin, ocf_pct")
        .eq("is_current", true)
        .order("fund_name");
      if (error) throw error;
      return (data ?? []) as FundOption[];
    },
    staleTime: 5 * 60_000,
  });

  const fundHouses = useQuery({
    queryKey: ["meeting-lookups", "fund-houses"],
    queryFn: async (): Promise<FundHouseOption[]> => {
      const { data, error } = await supabase
        .from("bm_fund_houses")
        .select("id, name, sort_order")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      // bm_fund_houses is benchmark-scoped: a house can recur across benchmarks.
      // Dedupe by name for the picker. (Note: partial universe — the form must keep
      // a free-text "new house" fallback for providers not on the benchmark list.)
      const seen = new Set<string>();
      const out: FundHouseOption[] = [];
      for (const r of (data ?? []) as Array<{ id: string; name: string }>) {
        if (seen.has(r.name)) continue;
        seen.add(r.name);
        out.push({ id: r.id, name: r.name });
      }
      return out;
    },
    staleTime: 5 * 60_000,
  });

  // meeting_fund_lookup: the searchable fund reference for the Meeting Request picker.
  // Cast via (supabase as any) — this table is newer than the committed types.ts. May be
  // EMPTY until an admin imports the fund list; the form degrades to free-text in that case.
  const meetingFunds = useQuery({
    queryKey: ["meeting-lookups", "meeting-funds"],
    queryFn: async (): Promise<MeetingFundOption[]> => {
      const { data, error } = await (supabase as any)
        .from("meeting_fund_lookup")
        .select("id, isin, fund_name, fund_house, fund_class, inc_acc, amc, ter_pct, sedol, available_on_abrdn")
        .eq("is_active", true)
        .order("fund_name");
      if (error) throw error;
      return (data ?? []) as MeetingFundOption[];
    },
    staleTime: 5 * 60_000,
  });

  const people = useQuery({
    queryKey: ["meeting-lookups", "people"],
    queryFn: async (): Promise<MeetingPerson[]> => {
      // get_meeting_people(): names only, author-gated server-side. Cast until
      // types.ts is regenerated (the RPC is newer than the committed types).
      const { data, error } = await (supabase.rpc as any)("get_meeting_people");
      if (error) throw error;
      return ((data ?? []) as MeetingPerson[]).filter((p) => p.is_active);
    },
    staleTime: 5 * 60_000,
  });

  return {
    funds: funds.data ?? [],
    fundHouses: fundHouses.data ?? [],
    meetingFunds: meetingFunds.data ?? [],
    people: people.data ?? [],
    isLoading: funds.isLoading || fundHouses.isLoading || meetingFunds.isLoading || people.isLoading,
    error: funds.error ?? fundHouses.error ?? meetingFunds.error ?? people.error ?? null,
  };
}
